import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SignalRService } from '../../core/services/signalr.service';

/**
 * Shows the realtime (SignalR) connection state in the toolbar — but only when something is
 * wrong. A healthy connection renders nothing at all: a permanent green dot is noise, and the
 * whole point of this indicator is that a DROPPED connection used to be completely invisible
 * (changes by other users simply stopped arriving, with the list on screen looking perfectly
 * normal).
 *
 * matTooltip, not tippyTooltip: matches its direct siblings in `IconMenuItemsComponent`
 * (action-watcher-button), the convention that toolbar already follows.
 */
@Component({
  selector: 'app-signalr-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  template: `
    @if (state() !== 'connected') {
      <div
        class="flex items-center justify-center w-10 h-10"
        [matTooltip]="tooltip()"
        role="status"
        [attr.aria-label]="tooltip()"
        data-cy="signalr-status">
        @if (state() === 'disconnected') {
          <!-- zap-off, not wifi-off: it is the realtime channel that is down, not the user's
               network — a wifi icon sends people to check their router. -->
          <mat-icon class="icon-size-5 text-warn" svgIcon="feather:zap-off"></mat-icon>
        } @else {
          <!-- The same mat-spinner every other "busy" surface in the app uses
               (notification-list, action-watcher), at the smallest of their diameters. -->
          <mat-spinner [diameter]="18"></mat-spinner>
        }
      </div>
    }
  `,
})
export class SignalRStatusComponent {
  protected readonly state = inject(SignalRService).connectionState;

  protected readonly tooltip = computed(() => {
    switch (this.state()) {
      case 'disconnected':
        return 'Geen live verbinding. Wijzigingen van anderen komen niet binnen. Opnieuw verbinden…';
      case 'reconnecting':
        return 'Live verbinding verbroken, opnieuw verbinden…';
      default:
        return 'Live verbinding wordt opgezet…';
    }
  });
}
